// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { createHandlerChainRunner, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { OrderUploadRowError } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { AddLineToWishList, chain } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { xngAddWishListLines, XngAddWishListLinesApiParameter } from "@werner/App/Services/WishListService";

export interface XngAddToWishListParameter {
    productInfos: Omit<ProductInfo, "productDetailPath">[];
    selectedWishList?: WishListModel;
    newListName?: string;
    onSuccess?: (wishList: WishListModel) => void;
    onError?: (errorMessage: string) => void;
    // XNG-Changes
    notFoundProducts?: OrderUploadRowError[];
    isUploadItemsButton?: boolean;
}

interface Props {
    result: {
        wishList?: WishListModel;
        errorMessage?: string;
    };
}

type HandlerType = Handler<XngAddToWishListParameter, Props>;

export const XngAddLineToWishList: HandlerType = async props => {
    if (!props.result.wishList) {
        return;
    }
    const notFoundProducts = props.parameter.notFoundProducts?.map(product => product.name);
    const isUploadItemsButton = props.parameter.isUploadItemsButton;
    try {
        const lines = props.parameter.productInfos.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        }));

        let params: XngAddWishListLinesApiParameter = {
            wishList: props.result.wishList,
            lines,
        };

        if (isUploadItemsButton) {
            params = {
                ...params,
                properties: {
                    isUploadItemsButton,
                    notFoundProducts: JSON.stringify(notFoundProducts),
                },
            };
        }
        await xngAddWishListLines({
            ...params,
        });
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        if (error?.status === 404) {
            props.result.errorMessage = error.errorMessage;
            return;
        }
        throw error;
    }
};

replaceInChain(chain, AddLineToWishList, XngAddLineToWishList);

const addToWishList = createHandlerChainRunner(chain, "AddToWishList");
export default addToWishList;
